/**
 * Readiness-monitor decoding — SAE J1979 Mode 01 PID 01 ("monitor status since
 * DTCs cleared"). This is the data behind "will my car pass the annual test?":
 * an OBD vehicle fails the inspection's OBD portion if required monitors are
 * "not ready", even with no fault codes.
 *
 * Response layout (4 data bytes A B C D after the `41 01` header):
 *   A7      MIL (check-engine light) commanded on
 *   A6..A0  confirmed emissions DTC count
 *   B0..B2  continuous-monitor availability (misfire, fuel system, components)
 *   B3      ignition type (0=spark, 1=compression)
 *   B4..B6  continuous-monitor completeness (0 = complete)
 *   C       non-continuous monitor availability (1 = supported)
 *   D       non-continuous monitor completeness (0 = complete)
 *
 * Only the spark-ignition C/D bit map is implemented in v1 (target vehicles
 * incl. the Sonata Hybrid are gasoline/spark). Compression-ignition diesels
 * use a different C/D layout and degrade to "continuous monitors only".
 */
function bit(value, n) {
    return ((value >> n) & 1) === 1;
}
function continuousMonitor(id, name, b, availBit, completeBit) {
    const supported = bit(b, availBit);
    // Completeness bit set to 0 means the test ran successfully this cycle.
    const ready = supported ? !bit(b, completeBit) : false;
    return { id, name, supported, ready };
}
function nonContinuousMonitor(id, name, c, d, n) {
    const supported = bit(c, n);
    const ready = supported ? !bit(d, n) : false;
    return { id, name, supported, ready };
}
/**
 * Parse a Mode 01 PID 01 response (cleaned bytes `41 01 A B C D`) into a
 * readiness report. Returns null on header mismatch or short data.
 */
export function parseReadinessMonitors(bytes) {
    if (bytes[0] !== 0x41 || bytes[1] !== 0x01)
        return null;
    const a = bytes[2];
    const b = bytes[3];
    const c = bytes[4];
    const d = bytes[5];
    if (a == null || b == null || c == null || d == null)
        return null;
    const milOn = bit(a, 7);
    const dtcCount = a & 0x7f;
    const compression = bit(b, 3);
    const monitors = [
        continuousMonitor('misfire', 'Misfire', b, 0, 4),
        continuousMonitor('fuelSystem', 'Fuel system', b, 1, 5),
        continuousMonitor('components', 'Components', b, 2, 6),
    ];
    if (!compression) {
        monitors.push(nonContinuousMonitor('catalyst', 'Catalyst', c, d, 0), nonContinuousMonitor('heatedCatalyst', 'Heated catalyst', c, d, 1), nonContinuousMonitor('evaporativeSystem', 'Evaporative system', c, d, 2), nonContinuousMonitor('secondaryAirSystem', 'Secondary air system', c, d, 3), nonContinuousMonitor('oxygenSensor', 'Oxygen sensor', c, d, 5), nonContinuousMonitor('oxygenSensorHeater', 'Oxygen sensor heater', c, d, 6), nonContinuousMonitor('egrSystem', 'EGR system', c, d, 7));
    }
    const notReady = monitors.filter((m) => m.supported && !m.ready);
    return {
        milOn,
        dtcCount,
        ignitionType: compression ? 'compression' : 'spark',
        monitors,
        readyForInspection: notReady.length === 0,
        notReady,
    };
}
//# sourceMappingURL=readiness.js.map